'use client';

import { Combobox } from '@systemeio/components';
import { ChevronDownIcon } from '@systemeio/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useFormControlSize } from '../form-control-size-context';
import { cancelBackspaceClear, getSelectedOption, OptionsSkeleton, type FormSelectDataType, type FormSelectIdType, type FormSelectMultipleProps } from './form-select';
interface FormSelectAsyncMultipleProps<T extends FormSelectIdType> extends FormSelectMultipleProps<T> {
  isFetching?: boolean;
  moreItems?: number;
  onSearchChange: (search: string) => void;
  search: string;
}
const MIN_SEARCH_LENGTH = 3;
function FormSelectAsyncMultiple<T extends FormSelectIdType>({
  ariaLabel,
  data,
  dataTest,
  description,
  disabled,
  error,
  inputRef,
  isFetching,
  isPreFetching,
  label,
  moreItems,
  onChange,
  onClose,
  onOpen,
  onSearchChange,
  placeholder,
  required,
  search,
  size,
  staticData,
  value,
  withoutCloseIcon
}: FormSelectAsyncMultipleProps<T>) {
  const inheritedSize = useFormControlSize();
  const {
    t
  } = useLocoTranslation();
  const selectedOptionsCacheRef = useRef(new Map<T, FormSelectDataType<T>>());
  const preserveQueryAfterSelectionRef = useRef(false);
  const selectedOptions = useMemo(() => value.map(selectedValue => getSelectedOption(selectedValue, data ?? staticData) ?? selectedOptionsCacheRef.current.get(selectedValue) ?? null).filter((option): option is FormSelectDataType<T> => option !== null), [data, staticData, value]);
  const selectedIds = new Set(value);
  const hasSearch = search.trim().length >= MIN_SEARCH_LENGTH;
  const isDisabled = disabled || isPreFetching;
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t('global.dynamic_search.start_typing');
  const accessibleLabel = ariaLabel ?? (typeof label === 'string' ? label : placeholder ?? t('global.select'));
  useEffect(() => {
    selectedOptionsCacheRef.current = new Map(selectedOptions.map(option_0 => [option_0.id, option_0] as const));
  }, [selectedOptions]);
  return <div aria-busy={isFetching || isPreFetching || undefined} className="grid w-full content-start gap-2">
      <Combobox.Root<FormSelectDataType<T>, true> aria-label={accessibleLabel} disabled={isDisabled} drawerTitle={label ?? resolvedPlaceholder}
    /* Results arrive already filtered by the server; Base UI must not filter again. */ filter={null} inputValue={search} isItemEqualToValue={(option_1, selected) => Object.is(option_1.id, selected.id)} itemToStringLabel={option_2 => option_2.caption} itemToStringValue={option_3 => String(option_3.id)} items={hasSearch ? data ?? [] : []} multiple onInputValueChange={(inputValue, eventDetails) => {
      if (preserveQueryAfterSelectionRef.current && eventDetails.reason === 'input-clear') {
        preserveQueryAfterSelectionRef.current = false;
        eventDetails.cancel();
        return;
      }
      preserveQueryAfterSelectionRef.current = false;
      onSearchChange(inputValue);
    }} onOpenChange={(open_0, eventDetails_0) => {
      // The mobile drawer closes without Base UI details (swipe, backdrop, breakpoint
      // flip), so reason is only readable when an originating event exists.
      if (!open_0 && eventDetails_0?.reason === 'item-press') {
        preserveQueryAfterSelectionRef.current = false;
        eventDetails_0.cancel();
        return;
      }
      setOpen(open_0);
      if (open_0) onOpen?.();else onClose?.();
    }} onValueChange={(options, eventDetails_1) => {
      if (cancelBackspaceClear(withoutCloseIcon, eventDetails_1)) return;
      preserveQueryAfterSelectionRef.current = eventDetails_1?.reason === 'item-press' && search.trim().length > 0;
      const nextOptions = options ?? [];
      selectedOptionsCacheRef.current = new Map(nextOptions.map(option_4 => [option_4.id, option_4] as const));
      onChange(nextOptions.map(option_5 => option_5.id));
    }} open={open} placeholder={resolvedPlaceholder} required={required} size={size ?? inheritedSize} value={selectedOptions}>
        {label !== undefined ? <Combobox.InputLabel required={required}>{label}</Combobox.InputLabel> : null}
        <Combobox.InputGroup aria-label={label === undefined ? accessibleLabel : undefined} className={isPreFetching ? 'animate-pulse' : undefined} data-invalid={error ? '' : undefined}>
          {selectedOptions.length > 0 ? <Combobox.Chips>
              {selectedOptions.map(option_6 => <Combobox.Chip id={String(option_6.id)} key={String(option_6.id)}>
                  {option_6.caption}
                  <Combobox.ChipRemove aria-label={`${t('global.delete')} ${option_6.caption}`} />
                </Combobox.Chip>)}
              <Combobox.OverflowBadge />
              {/* Empty placeholder: the mobile field already shows the chips. */}
              <Combobox.Input {...dataTest} placeholder="" ref={inputRef} />
            </Combobox.Chips> : <Combobox.Input {...dataTest} ref={inputRef} />}
          {!withoutCloseIcon && selectedOptions.length > 0 && !isDisabled ? <Combobox.Clear aria-label={t('global.clear')} /> : null}
          <Combobox.Trigger aria-label={accessibleLabel}>
            <ChevronDownIcon />
          </Combobox.Trigger>
        </Combobox.InputGroup>
        <Combobox.Content>
          {!hasSearch ? <Combobox.Empty description={t('dashboard.droplist.default_description')}>
              {t('global.dynamic_search.start_typing')}
            </Combobox.Empty> : isFetching || data === undefined ? <OptionsSkeleton /> : <>
              <Combobox.Empty description={t('dashboard.droplist.default_description')}>
                {t('dashboard.droplist.default_title')}
              </Combobox.Empty>
              <Combobox.List>
                {(option_7: FormSelectDataType<T>) => <Combobox.Item data-test-id={option_7.dataTestAttribute} disabled={option_7.disabled} key={String(option_7.id)} value={option_7}>
                    <Combobox.ItemCheckbox checked={selectedIds.has(option_7.id)} />
                    <Combobox.ItemText>{option_7.customCaption ?? option_7.caption}</Combobox.ItemText>
                    {option_7.additionalRender}
                  </Combobox.Item>}
              </Combobox.List>
            </>}
          {hasSearch && moreItems && !isFetching ? <Combobox.Status>
              {t('global.dynamic_search.has_more_results', {
            count: moreItems
          })}
            </Combobox.Status> : null}
        </Combobox.Content>
      </Combobox.Root>
      {description ? <Combobox.Description>{description}</Combobox.Description> : null}
      {error ? <Combobox.Error>{error}</Combobox.Error> : null}
    </div>;
}
export { FormSelectAsyncMultiple };
export type { FormSelectAsyncMultipleProps };